import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import Marquee from 'react-fast-marquee';
import { BsShareFill } from 'react-icons/bs';
import * as APIHelper from '../../../Helper/apiFetch';
import * as nativeEvents from '../../../Helper/native';
import ValentinesFAQ from './ValentinesFAQ';
import ValentinesTAndC from './ValentinesTAndC';
import ScratchCard from './ScratchCard';
import Congratulation from '../../congratulation';
import { formatTimerDisplay, formatNumberWithCommas } from '../utils';
import TopFiveLeaderboard from './TopFiveLeaderboard';
import { ASSET_URLS, BUTTON_SPINNER_STYLE, CAMPAIGN_CONFIG } from '../constants';
import Loader from './Loader';
const initiaData = require("../../urlEncodeTest.json");

const ValentinesBanner = ({ showBrandsScreen = false, remainingTime = { hours: 0, minutes: 0, seconds: 0, days: 0 }, onShare, cardType, cardPaymentStatus }) => {
  const navigate = useNavigate();
  const [campaignConfig, setCampaignConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [congratsPopup, setCongratsPopup] = useState(false);
  const [congratsTitle, setCongratsTitle] = useState({ title: 'Congratulations!', msg1: '' });

  useEffect(() => {
    const fetchCampaignConfig = async () => {
      const startTime = Date.now();
      const MIN_LOADING_MS = 600;
      try {
        setLoading(true);
        const currentToken = sessionStorage.getItem('AccessToken');
        
        if (!currentToken) {
          console.log("No access token available");
          setLoading(false);
          return;
        }

        const response = await APIHelper.topFive();
        // const response = initiaData.anniversaryCampaign;
        if (response && !response.error) {
          const data = response.data ?? response;
          setCampaignConfig(data);
          const elapsed = Date.now() - startTime;
          const remaining = Math.max(0, MIN_LOADING_MS - elapsed);
          setTimeout(() => setLoading(false), remaining);
        } else {
          setLoading(false);
          navigate('/error');
        }
      } catch (error) {
        console.error('Error fetching campaign config:', error);
        setLoading(false);
        navigate('/error');
      }
    };

    fetchCampaignConfig();
  }, []);

  const loyaltyBonusData = campaignConfig?.anniversaryCampaign?.loyaltyBonus;
  const cardSpendData = campaignConfig?.anniversaryCampaign?.cardSpend;

  // Loyalty bonus & voucher streak sections visible until 23rd Feb 11:59:59 PM IST, then hidden
  const rewardCutoff = new Date('2026-02-23T23:59:59+05:30'); // Feb 23, 2026 11:59:59 PM IST
  const showUntilCutoff = new Date() <= rewardCutoff;
  const cardPurchaseData = campaignConfig?.anniversaryCampaign?.cardPurchase;
  const voucherStreakData = campaignConfig?.anniversaryCampaign?.voucherStreak;
  const goldPurchaseData = campaignConfig?.anniversaryCampaign?.goldPurchase;

  const getLoyaltyBonusContent = () => {
    if (!loyaltyBonusData) return null;

    const loyaltyBonusAmount = loyaltyBonusData?.loyaltyBonus ?? 0;
    const extraBonusAmount = loyaltyBonusData?.extraBonus ?? 0;
    const loyaltyRedeemed = loyaltyBonusData?.loyaltyRedeemed ?? false;
    const extraRedeemed = loyaltyBonusData?.extraRedeemed ?? false;
    const eligibleForExtraBonus = loyaltyBonusData?.eligibleForExtraBonus ?? false;

    // Helper: claimed content when user has claimed
    const getClaimedContent = () => {
      const claimedAmount = (loyaltyBonusAmount || 0) + (extraBonusAmount || 0);
      return {
        title: claimedAmount > 0 ? `You have claimed your reward of ${claimedAmount} Sats` : "You have claimed your reward",
        showButton: false
      };
    };

    // Case 4: loyaltyBonus <= 0 && !loyaltyRedeemed && extraBonus > 0 && extraRedeemed === true
    // Check this before case 5 since it's a specific subset
    if (loyaltyBonusAmount <= 0 && !loyaltyRedeemed && extraBonusAmount > 0 && extraRedeemed) {
      return {
        title: `You have earned ${extraBonusAmount} Sats`,
        buttonText: "CLAIM NOW",
        showButton: true
      };
    }

    // Case 3: No reward enabled - show "Still you can earn 100 sats" only if eligibleForExtraBonus, else show claimed if applicable
    if (loyaltyBonusAmount <= 0 && extraBonusAmount <= 0) {
      if (eligibleForExtraBonus) {
        return {
          title: "Still you can earn 100 sats by spending",
          buttonText: "SPEND NOW",
          showButton: true
        };
      }
      if (loyaltyRedeemed || extraRedeemed) {
        return getClaimedContent();
      }
      return null;
    }

    // Case 5: Both redeemed OR (loyaltyRedeemed false and extraRedeemed true)
    if ((loyaltyRedeemed && extraRedeemed) || (!loyaltyRedeemed && extraRedeemed)) {
      return getClaimedContent();
    }

    // Case 2: extraBonus > 0 && !extraRedeemed -> show total (loyaltyBonus + extraBonus) if both available, otherwise just extraBonus
    if (extraBonusAmount > 0 && !extraRedeemed) {
      // If both loyaltyBonus and extraBonus are available and not redeemed, show total
      if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
        const total = loyaltyBonusAmount + extraBonusAmount;
        return {
          title: `Claim your ${total} Sats Bonus!`,
          buttonText: "CLAIM NOW",
          showButton: true
        };
      }
      // If only extraBonus is available, show just extraBonus
      return {
        title: `Claim your ${extraBonusAmount} sats`,
        buttonText: "CLAIM NOW",
        showButton: true
      };
    }

    // Case 1: loyaltyBonus > 0 && !loyaltyRedeemed (only if extraBonus doesn't apply)
    if (loyaltyBonusAmount > 0 && !loyaltyRedeemed) {
      return {
        title: `Claim your ${loyaltyBonusAmount} Sats Loyalty Bonus!`,
        buttonText: "CLAIM NOW",
        showButton: true
      };
    }

    // Case 6: loyaltyRedeemed true, extraRedeemed false, extraBonus 0 -> user claimed loyalty but can still earn extra
    if (loyaltyRedeemed && !extraRedeemed && extraBonusAmount === 0) {
      if (eligibleForExtraBonus) {
        return {
          title: "Still you can earn 100 sats more",
          buttonText: "SPEND NOW",
          showButton: true
        };
      }
      return getClaimedContent();
    }

    return null;
  };

  const getCardSpendContent = () => {
    if (!cardSpendData) return null;
    const cardIssued = String(campaignConfig?.cardIssued || cardType || '').toUpperCase();
    
    if (cardIssued === 'INTRO') {
      return {
        title: "Get 5% back on Fashion, Food & Travel spends",
        buttonText: "UPGRADE NOW",
        showButton: true,
        onButtonClick: () => campaignConfig?.cardPaymentStatus ? nativeEvents.upgradeCard() : nativeEvents.purchaseCard()
      };
    }
    if (cardIssued === 'NOCARD') {
      return {
        title: "Get 5% back on Fashion, Food & Travel spends",
        buttonText: "BUY NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.purchaseCard()
      };
    }
    return {
      title: "Get 5% back on Fashion, Food & Travel spends",
      buttonText: "SPEND NOW",
      showButton: true,
      onButtonClick: () => nativeEvents.loadCard()
    };
  };

  const getCardPurchaseContent = () => {
    if (!cardPurchaseData) return null;
    const cardIssued = String(cardPurchaseData?.cardIssued || cardType || '').toUpperCase();
    if (cardIssued === 'INTRO') {
      return {
        title: "Get Elite Card at ₹299",
        buttonText: "UPGRADE NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.upgradeCard()
      };
    }
    if (cardIssued === 'NOCARD') {
      return {
        title: "Get Elite Card at ₹499",
        buttonText: "BUY NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.purchaseCard()
      };
    }
    return {
      title: "Card Purchase Reward",
      showButton: false
    };
  };

  const getVoucherStreakContent = () => {
    if (!voucherStreakData) return null;
    const voucherBonus = voucherStreakData?.voucherBonus ?? 0;
    const voucherBonusRedeemed = voucherStreakData?.voucherBonusRedeemed || false;
    const txnsCount = voucherStreakData?.txns?.length ?? 0;
    const remaining = Math.max(0, 5 - txnsCount);
    const buyMoreTitle = remaining > 0
      ? `Buy ${remaining} more voucher${remaining === 1 ? '' : 's'} and earn 500 Sats instantly`
      : "Buy 5 vouchers today and earn 500 Sats instantly";
    
    if (voucherBonus > 0 && !voucherBonusRedeemed) {
      return {
        title: `Claim your ${voucherBonus} Sats Voucher Streak Bonus!`,
        buttonText: "CLAIM NOW",
        showButton: true,
        onButtonClick: () => {
          const bonus = voucherStreakData?.voucherBonus || 0;
          if (bonus > 0) handleClaim('voucherStreak', bonus);
        }
      };
    }
    // voucherBonus present and redeemed - show claimed message
    if (voucherBonus > 0 && voucherBonusRedeemed) {
      return {
        title: `You have claimed ${voucherBonus} Sats`,
        showButton: false
      };
    }
    // voucherBonus is 0 or undefined - show BUY NOW, redirect to offersBrands
    if (voucherBonus === 0 || voucherBonus === undefined) {
      return {
        title: buyMoreTitle,
        buttonText: "BUY NOW",
        showButton: true,
        onButtonClick: () => nativeEvents.offersBrands()
      };
    }
    return {
      title: buyMoreTitle,
      showButton: false
    };
  };

  const getGoldPurchaseContent = () => {
    if (!goldPurchaseData) return null;
    return {
      title: "Buy Gold instantly with 5% off up to ₹50 (min ₹500)",
      showButton: true,
      buttonText: "BUY NOW",
      onButtonClick: () => nativeEvents.lumpsumScreen()
    };
  };

  const handleReveal = (revealType) => {
    APIHelper.updateAnniversaryReveal(revealType)
      .then((response) => {
        if (response && !response.error) {
          const data = response.data ?? response;
          setCampaignConfig((prev) => {
            if (!prev) return data;
            const merged = { ...prev };
            const updated = data?.anniversaryCampaign ?? data;
            if (updated && typeof updated === 'object') {
              const prevCampaign = prev.anniversaryCampaign || {};
              merged.anniversaryCampaign = { ...prevCampaign };
              Object.keys(updated).forEach((key) => {
                const val = updated[key];
                if (val && typeof val === 'object' && !Array.isArray(val)) {
                  merged.anniversaryCampaign[key] = { ...(prevCampaign[key] || {}), ...val };
                } else {
                  merged.anniversaryCampaign[key] = val;
                }
              });
              if (revealType && merged.anniversaryCampaign[revealType]) {
                merged.anniversaryCampaign[revealType] = { ...merged.anniversaryCampaign[revealType], isRevealed: true };
              }
            }
            return merged;
          });
        } else {
          console.error('Error revealing:', response?.error || 'Unknown error');
          navigate('/error');
        }
      })
      .catch((error) => {
        console.error('Error calling reveal API:', error);
        navigate('/error');
      });
  };

  const handleClaim = async (claimType, amount) => {
    try {
      setClaiming(claimType);
      const response = await APIHelper.updateAnniversaryClaim(claimType, amount);
      
      if (response && !response.error) {
        const data = response.data ?? response;
        let rewardAmount = 0;
        if (claimType === 'voucherStreak') {
          const voucherData = data?.anniversaryCampaign?.voucherStreak;
          rewardAmount = voucherData?.voucherBonus ?? data?.rewards ?? data?.sats ?? amount ?? 0;
        } else if (claimType === 'loyaltyBonus') {
          const loyaltyData = data?.anniversaryCampaign?.loyaltyBonus;
          const lb = loyaltyData?.loyaltyBonus ?? 0;
          const eb = loyaltyData?.extraBonus ?? 0;
          rewardAmount = (lb + eb) || (data?.rewards ?? data?.sats ?? amount ?? 0);
        }
        rewardAmount = Number(rewardAmount) || 0;
        setCampaignConfig((prev) => {
          if (!prev) return data;
          const merged = { ...prev };
          const updated = data?.anniversaryCampaign ?? data;
          if (updated && typeof updated === 'object') {
            const prevCampaign = prev.anniversaryCampaign || {};
            merged.anniversaryCampaign = { ...prevCampaign };
            Object.keys(updated).forEach((key) => {
              const val = updated[key];
              if (val && typeof val === 'object' && !Array.isArray(val)) {
                merged.anniversaryCampaign[key] = { ...(prevCampaign[key] || {}), ...val };
              } else {
                merged.anniversaryCampaign[key] = val;
              }
            });
          }
          return merged;
        });
        setCongratsTitle({
          title: 'Congratulations!',
          msg1: claimType === 'voucherStreak'
            ? `You've earned <b>${rewardAmount}</b> Sats for completing 5 vouchers!`
            : `You've earned <b>${rewardAmount}</b> Sats as Loyalty Bonus!`,
        });
        setCongratsPopup(true);
      } else {
        console.error('Error claiming:', response?.error || 'Unknown error');
        navigate('/error');
      }
    } catch (error) {
      console.error('Error calling claim API:', error);
      navigate('/error');
    } finally {
      setClaiming(null);
    }
  };

  const loyaltyContent = getLoyaltyBonusContent();
  const cardSpendContent = getCardSpendContent();
  const cardPurchaseContent = getCardPurchaseContent();
  const voucherStreakContent = getVoucherStreakContent();
  const goldPurchaseContent = getGoldPurchaseContent();

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="valentines-banner-container">
      <div className="headderMarquee">
        <div className="leftLayoutMonth"></div>
        <Marquee speed={30} gradientColor="none" direction="left">
          <div className="wazirxHeadderTimerTitle">
            Hurry! Holi Campaign is Ending Soon &nbsp;&nbsp;
          </div>
        </Marquee>
        <div className="campTimerContainer" style={{ width: '130px', marginLeft: '0px' }}>
          <img alt=" " className="timerIcon" src={ASSET_URLS.TIMER_ICON} />
          <div className="timeCounter">
            {remainingTime === 0 ? (
              '00:00:00'
            ) : (
              <div style={{ display: 'flex', gap: '2px', marginBottom: '2px' }}>
                {formatTimerDisplay(remainingTime)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="headerCta">
        <button
          className="shareCta"
          onClick={() => onShare && onShare(initiaData.floatingShareMsg.msg)}
        >
          <BsShareFill size={22} color="#4B2382" />
        </button>
      </div>
      <div className="valentines-offer-till-text">
        Offer till 5th March
      </div>
      {
        campaignConfig?.cardPaymentStatus && campaignConfig?.cardIssued === 'ELITE' ? (
          <div className="valentines-subtitle">
            Your Spending just got more rewarding.
          </div>
        ) : (
          <div className="valentines-subtitle">
            Upgrade and get 40% off on Elite card purchase
          </div>
        )
      }
      <div className="valentine-brands-subtitle">
        Earn 3% rewards on every spend.
      </div>
      <div className="valentines-image">
        <img alt=" " className="valentines-image-img" src="https://assets.gosats.io/notification/2026/3/1772446939067" />
      </div>
      <div className="valentines-ctaButtonContainer">
        {
          campaignConfig?.cardPaymentStatus && campaignConfig?.cardIssued === 'ELITE' ? (
            <div>
              <button className="valentines-ctaButton" onClick={() => nativeEvents.loadCard()}>EARN 3% REWARDS</button>
            </div>
          ) : (
            <div>
              <button
                className="valentines-ctaButton"
                onClick={() => {
                  if (campaignConfig?.cardIssued === 'INTRO' && campaignConfig?.cardPaymentStatus) {
                    nativeEvents.upgradeCard();
                  } else if (campaignConfig?.cardIssued === 'INTRO' && !campaignConfig?.cardPaymentStatus) {
                    nativeEvents.purchaseCard();
                  } else if (campaignConfig?.cardIssued === 'NOCARD') {
                    nativeEvents.activateCard();
                  } else {
                    nativeEvents.upgradeCard();
                  }
                }}
              >
                {campaignConfig?.cardIssued === 'NOCARD' ? 'ACTIVATE NOW' : 'UPGRADE TO ELITE CARD'}
              </button>
            </div>
          )
        }
      </div>
      <div className="footerContainer" style={{ marginTop: '0px' }}>
        <div style={{ padding: '0px 0px 50px 0px' }}>
          <div style={{ marginBottom: '28px', marginTop: '40px' }}>
            <ValentinesFAQ />
          </div>
          <ValentinesTAndC />
        </div>
      </div>

      {/* {congratsPopup && (
        <Congratulation
          title={congratsTitle}
          onAction={() => {
            setCongratsPopup(false);
            window.location.href = '/home';
          }}
          onClose={() => {
            setCongratsPopup(false);
            window.location.href = '/home';
          }}
        />
      )} */}
    </div>
  )
};

export default ValentinesBanner;
